import { getGlobalState, getLayersState } from '@gui/store/persistent/persistentStore.js';
import { toCurrentCoordinate } from '@data/geodata/CoordinateSystemUtils';
import { grid } from '../Global/global_variables.js';
import { Frame } from '../Simulations/Frame.js';
import FVector from '@data/geometry/FVector';
import type { CoordinateSystemSelection } from '@visualizer/types';

export class DataExporter {
    getTextData(variant: any, _format: string, selectedSPs?: Set<string>, coordinateSystem?: CoordinateSystemSelection): string[][] {
        const safe = (val: any) => (val !== undefined && val !== null ? val.toFixed(2) : '');
        const currentOrigin = toCurrentCoordinate(new FVector(0, 0, 0), coordinateSystem);

        // Used to maintain only rotation (for directions, which don't care of translation):
        const removeTranslation = (transformed: FVector): FVector => {
            return new FVector(transformed.x - currentOrigin.x, transformed.y - currentOrigin.y, transformed.z - currentOrigin.z);
        };

        var exportAllSensors = true;
        var exportSelectedSensors = false;

        switch (variant) {
            case 'custom':
                exportSelectedSensors = true;
            case 'currentView':
                exportAllSensors = false;
                break;
            case 'everything':
                break;
        }

        // get all selected sensorData
        // convert sensorData into a 3D model with or without meta-data
        const framesToExport: Frame[] = [];
        const framesLayerName: string[] = [];
        if (exportAllSensors) {
            for (let geodata of grid.geodatas) {
                for (let simulationSet of geodata.simulationSets) {
                    for (let layer of simulationSet.layerCollection) {
                        let frames = layer.getFrameCollection();
                        for (let frame of frames) {
                            framesToExport.push(frame);
                            framesLayerName.push(layer.name);
                        }
                    }
                }
            }
        } else if (exportSelectedSensors) {
            let selection = Array.from(selectedSPs || []);
            for (let sel of selection) {
                //layer_0_0_0_12;
                const splits = sel.split('-');
                const geodatasIndex = splits.length > 1 ? Number(splits[1]) : -1;
                const simulationSetIndex = splits.length > 2 ? Number(splits[2]) : -1;
                const layerIndex = splits.length > 3 ? Number(splits[3]) : -1;
                let frameIndex = splits.length > 5 ? Number(splits[5]) : -1;
                if (layerIndex == -1) continue; // this means that all previous indices are != -1
                if (frameIndex == -1) frameIndex = 0; // if the layer only had one frame, we didnt save the frameIndex
                const layer = grid.geodatas[geodatasIndex].simulationSets[simulationSetIndex].layerCollection[layerIndex];
                const frame = layer.getFrameCollection()[frameIndex];
                framesToExport.push(frame);
                framesLayerName.push(layer.name);
            }
        } else {
            // ONLY WHAT'S VISIBLE
            if (getGlobalState().data.multimode) {
                for (const mixerLayer of getLayersState().layerStates.mixed?.mixerElements) {
                    const layer = grid?.searchLayerByUID?.(mixerLayer.layerId);
                    if (!layer) continue;
                    framesToExport.push(layer.getActiveFrame());
                    framesLayerName.push(layer.name);
                }
            } else {
                // mixer or individual layers
                const frame = grid.getActiveLayer().getActiveFrame();
                if (frame) {
                    framesToExport.push(frame);
                    framesLayerName.push(grid.getActiveLayer().name);
                }
            }
        }

        let outputTextTable: string[][] = [];
        outputTextTable.push([
            'Layer Name',
            'Frame Name',
            'Center X',
            'Center Y',
            'Center Z',
            'Scalar Value',
            'Module Value',
            'Original Scalar Value',
            'Original Module Value',
            'Vectorial Value X',
            'Vectorial Value Y',
            'Vectorial Value Z',
            'Original Vectorial Value X',
            'Original Vectorial Value Y',
            'Original Vectorial Value Z',
        ]);

        for (let i = 0; i < framesToExport.length; i++) {
            let frame = framesToExport[i];
            if (!frame) continue;
            let layerName = framesLayerName[i];
            let frameName = frame.getName();
            let sensors = frame.getSensors();

            for (let sensor of sensors) {
                if (sensor.getScalarValue() == 0) continue;
                const center = toCurrentCoordinate(sensor.getPosition(), coordinateSystem);
                const direction = removeTranslation(toCurrentCoordinate(sensor.getDirection(), coordinateSystem));
                const scalarValue = sensor.getScalarValue();
                const moduleValue = direction.mag();
                const originalDirection = removeTranslation(toCurrentCoordinate(sensor.getVectorialResult(), coordinateSystem));
                const originalScalarValue = scalarValue;
                const originalModuleValue = originalDirection.mag();

                let row: string[] = [];
                row.push(layerName);
                row.push(frameName);
                row.push(safe(center.x));
                row.push(safe(center.y));
                row.push(safe(center.z));
                row.push(safe(scalarValue));
                row.push(safe(moduleValue));
                row.push(safe(originalScalarValue));
                row.push(safe(originalModuleValue));
                row.push(safe(direction.x));
                row.push(safe(direction.y));
                row.push(safe(direction.z));
                row.push(safe(originalDirection.x));
                row.push(safe(originalDirection.y));
                row.push(safe(originalDirection.z));
                outputTextTable.push(row);
            }
        }
        return outputTextTable;
    }

    getFormattedData(data: string[][], format: string): string {
        if (!data || data.length === 0) return '';

        let formattedData: string = '';
        if (data.length > 0) {
            switch (format.toLowerCase()) {
                case 'csv':
                    formattedData = data.map((row) => row.join(',')).join('\n');
                    break;
                case 'json':
                    formattedData = JSON.stringify(data, null, 2);
                    break;
                case 'tsv':
                    formattedData = data.map((row) => row.join('\t')).join('\n');
                    break;
            }
        }
        return formattedData;
    }
}
